// Layer: Page (UI) – chỉ render JSX, toàn bộ logic chấm công nằm trong hook domain `useTimesheetTable`.
import React, { useMemo } from 'react';
import { Typography, Button, Table, Spin, Avatar } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Download, User } from 'lucide-react';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { FilterSection } from '../components/common/FilterSection';
import { Pagination } from '../components/common/Pagination';
import type { DayData } from '@/features/timekeeping/types';
import { isSunday } from '@/features/timekeeping/summary';
import {
  useTimesheetTable,
  type TimesheetTableRecord,
  type UseTimesheetTableResult,
} from '@/features/timekeeping/useTimesheetTable';
import styles from './TimesheetTablePage.module.css';
import { loadAvatarFromData } from '@/utils/avatarLoader';

dayjs.locale('vi');

const { Text } = Typography;

// Hàm render cell từng ngày trong bảng công (thuần UI)
const renderDayCell = (dayData: DayData) => {
  if (isSunday(dayData.date)) {
    return null; // Chủ Nhật không hiển thị gì
  }

  const indicators: React.ReactNode[] = [];

  if (dayData.status === 'absent') {
    return (
      <div className={`${styles.statusBadge} ${styles.statusBadgeAbsent}`}>
        V
      </div>
    );
  }

  if (dayData.status === 'leave') {
    return (
      <div className={`${styles.statusBadge} ${styles.statusBadgeLeave}`}>
        P
      </div>
    );
  }

  if (dayData.status === 'unpaidLeave') {
    return (
      <div className={`${styles.statusBadge} ${styles.statusBadgeUnpaidLeave}`}>
        P
      </div>
    );
  }

  if (dayData.status === 'pending') {
    return (
      <div className={`${styles.statusBadge} ${styles.statusBadgePending}`}>
        ?
      </div>
    );
  }

  if (dayData.status === 'today') {
    return (
      <div className={`${styles.statusBadge} ${styles.statusBadgeToday || ''}`}>
        -
      </div>
    );
  }

  if (dayData.coefficient !== undefined) {
    indicators.push(
      <span key="coefficient" className={styles.coefficientDot}>
        <span className={styles.dot}>•</span> {dayData.coefficient.toFixed(3)}
      </span>
    );
  }

  if (dayData.lateMinutes) {
    indicators.push(
      <span key="late" className={styles.lateDot}>
        <span className={styles.dot}>•</span> {dayData.lateMinutes}'
      </span>
    );
  }

  if (dayData.overtimeHours && dayData.overtimeHours > 0) {
    indicators.push(
      <span key="overtime" className={styles.overtimeDot}>
        <span className={styles.dot}>•</span> {dayData.overtimeHours}h
      </span>
    );
  }

  if (indicators.length === 0) {
    return null;
  }

  return <div className={styles.dayCell}>{indicators}</div>;
};

const TimesheetEmployeeAvatar: React.FC<{ avatar?: string | null }> = ({ avatar }) => {
  const [avatarSrc, setAvatarSrc] = React.useState<string | null>(null);
  const [useFallback, setUseFallback] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;
    setAvatarSrc(null);
    setUseFallback(false);

    if (!avatar) {
      return;
    }

    loadAvatarFromData(avatar)
      .then((src) => {
        if (!isMounted) return;
        if (src) {
          setAvatarSrc(src);
        } else {
          setUseFallback(true);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setUseFallback(true);
      });

    return () => {
      isMounted = false;
    };
  }, [avatar]);

  const src = !useFallback && avatarSrc ? avatarSrc : undefined;

  return (
    <Avatar
      size={32}
      src={src}
      icon={!src ? <User size={16} /> : undefined}
      onError={() => {
        setUseFallback(true);
        setAvatarSrc(null);
        return false;
      }}
    />
  );
};

export const TimesheetTablePage: React.FC = () => {
  const {
    searchText,
    setSearchText,
    selectedDepartment,
    setSelectedDepartment,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    hasActiveFilter,
    departments,
    allDays,
    loading,
    tableData,
    paginatedData,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    selectedEmployee,
    setSelectedEmployee,
    selectedEmployeeSummary,
    selectedEmployeeInfo,
    handleClearFilter,
    handleExport,
    error,
  }: UseTimesheetTableResult = useTimesheetTable();

  const columns: ColumnsType<TimesheetTableRecord> = useMemo(() => {
    const cols: ColumnsType<TimesheetTableRecord> = [
      {
        title: 'Nhân viên',
        key: 'employee',
        fixed: 'left',
        width: 220,
        render: (_: any, record: TimesheetTableRecord) => (
          <div
            className={`${styles.employeeCell} ${selectedEmployee === record.key ? styles.employeeCellSelected : ''}`}
          >
            <TimesheetEmployeeAvatar avatar={record.avatar} />
            <div className={styles.employeeInfo}>
              <Text strong={selectedEmployee === record.key} className={styles.employeeName}>
                {record.employeeName}
              </Text>
              <Text type="secondary" className={styles.employeeCode}>
                {record.employeeCode}
              </Text>
            </div>
          </div>
        ),
      },
      {
        title: 'Tổng',
        key: 'total',
        width: 100,
        align: 'center',
        render: (_: any, record: TimesheetTableRecord) => {
          if (!record.summary) return null;
          return (
            <div className={`${styles.summaryBoxInTable} ${styles.summaryBoxInTableTotal}`}>
              <div className={styles.summaryBoxValueInTable}>
                {record.summary.total}
              </div>
            </div>
          );
        },
      },
      {
        title: 'Tăng ca',
        key: 'overtime',
        width: 100,
        align: 'center',
        render: (_: any, record: TimesheetTableRecord) => {
          if (!record.summary) return null;
          return (
            <div className={`${styles.summaryBoxInTable} ${styles.summaryBoxInTableOvertime}`}>
              <div className={styles.summaryBoxValueInTable}>
                {record.summary.overtime}h
              </div>
            </div>
          );
        },
      },
      {
        title: 'Vi phạm',
        key: 'violation',
        width: 100,
        align: 'center',
        render: (_: any, record: TimesheetTableRecord) => {
          if (!record.summary) return null;
          return (
            <div className={`${styles.summaryBoxInTable} ${styles.summaryBoxInTableViolation}`}>
              <div className={styles.summaryBoxValueInTable}>
                {record.summary.violation}'
              </div>
            </div>
          );
        },
      },
      {
        title: 'Nghỉ',
        key: 'leave',
        width: 100,
        align: 'center',
        render: (_: any, record: TimesheetTableRecord) => {
          if (!record.summary) return null;
          return (
            <div className={`${styles.summaryBoxInTable} ${styles.summaryBoxInTableLeave}`}>
              <div className={styles.summaryBoxValueInTable}>
                {record.summary.leave}
              </div>
            </div>
          );
        },
      },
      {
        title: 'Phụ cấp',
        key: 'mealAllowance',
        width: 120,
        align: 'center',
        render: (_: any, record: TimesheetTableRecord) => {
          if (!record.summary) return null;
          return (
            <div className={`${styles.summaryBoxInTable} ${styles.summaryBoxInTableMealAllowance}`}>
              <div className={styles.summaryBoxValueInTable}>
                {record.summary.mealAllowance}
              </div>
            </div>
          );
        },
      },
      {
        title: 'Tổng thực tế',
        key: 'actualTotal',
        width: 120,
        align: 'center',
        render: (_: any, record: TimesheetTableRecord) => {
          if (!record.summary) return null;
          return (
            <div className={`${styles.summaryBoxInTable} ${styles.summaryBoxInTableActualTotal}`}>
              <div className={styles.summaryBoxValueInTable}>
                {record.summary.actualTotal}
              </div>
            </div>
          );
        },
      },
    ];

    // Add day columns
    allDays.forEach((day) => {
      const isDaySunday = isSunday(day.date);
      cols.push({
        title: day.dayNumber.toString(),
        key: `day_${day.dayNumber}`,
        width: 120,
        align: 'center',
        className: isDaySunday ? styles.sundayColumn : '',
        render: (_: any, record: TimesheetTableRecord) => {
          const dayData = record[`day_${day.dayNumber}`] as DayData | null;
          return dayData ? renderDayCell(dayData) : null;
        },
      });
    });

    return cols;
  }, [allDays, selectedEmployee]);

  return (
    <div className={styles.container}>
      <FilterSection
        title="Bộ lọc"
        subtitle="Tìm kiếm và lọc dữ liệu chấm công"
        searchPlaceholder="Tìm kiếm theo tên, mã NV, ..."
        searchValue={searchText}
        onSearchChange={setSearchText}
        showDepartmentFilter={true}
        selectedDepartment={selectedDepartment}
        onDepartmentChange={setSelectedDepartment}
        showMonthFilter={true}
        selectedMonth={selectedMonth}
        onMonthChange={setSelectedMonth}
        showYearFilter={true}
        selectedYear={selectedYear}
        onYearChange={setSelectedYear}
        onClearFilter={handleClearFilter}
        isFilterActive={hasActiveFilter}
        departments={departments as any}
        extraActions={
          <Button
            icon={<Download size={16} />}
            onClick={handleExport}
            className={styles.exportButton}
            size="large"
          >
            Xuất file
          </Button>
        }
      />

      <div className={styles.contentWrapper}>
        <div className={styles.tableCard}>
          <Spin spinning={loading}>
            {/* Nếu hook trả về error, hiển thị message ở trên bảng */}
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}
            <Table
              columns={columns}
              dataSource={paginatedData}
              rowKey="key"
              pagination={false}
              className={styles.table}
              scroll={{ x: 'max-content' }}
              rowClassName={(record) => selectedEmployee === record.key ? styles.selectedRow : ''}
              onRow={(record) => ({
                onClick: () => {
                  setSelectedEmployee(record.key);
                },
                className: styles.clickableRow,
              })}
            />
          </Spin>
          <Pagination
            current={currentPage}
            total={tableData.length}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            itemName="nhân viên"
            pageSizeOptions={['10', '20', '50', '100']}
          />
        </div>

        {selectedEmployee && selectedEmployeeSummary && selectedEmployeeInfo && (
          <div className={styles.summaryCard}>
            <div className={styles.summaryHeader}>
              <div>
                <Text strong className={styles.summaryHeaderTitle}>{selectedEmployeeInfo.employeeName}</Text>
                <div>
                  <Text type="secondary">{selectedEmployeeInfo.employeeCode}</Text>
                </div>
              </div>
              <Button
                type="text"
                onClick={() => setSelectedEmployee(null)}
                className={styles.summaryCloseButton}
              >
                ✕
              </Button>
            </div>
            <div className={styles.summaryContent}>
              <div className={styles.summaryGrid}>
                <div className={`${styles.summaryBox} ${styles.summaryBoxTotal}`}>
                  <div className={styles.summaryBoxLabel}>Tổng</div>
                  <div className={styles.summaryBoxValue}>
                    {selectedEmployeeSummary.total}
                  </div>
                </div>
                <div className={`${styles.summaryBox} ${styles.summaryBoxOvertime}`}>
                  <div className={styles.summaryBoxLabel}>Tăng ca</div>
                  <div className={styles.summaryBoxValue}>
                    {selectedEmployeeSummary.overtime}h
                  </div>
                </div>
                <div className={`${styles.summaryBox} ${styles.summaryBoxViolation}`}>
                  <div className={styles.summaryBoxLabel}>Vi phạm</div>
                  <div className={styles.summaryBoxValue}>
                    {selectedEmployeeSummary.violation}'
                  </div>
                </div>
                <div className={`${styles.summaryBox} ${styles.summaryBoxLeave}`}>
                  <div className={styles.summaryBoxLabel}>Nghỉ</div>
                  <div className={styles.summaryBoxValue}>
                    {selectedEmployeeSummary.leave}
                  </div>
                </div>
                <div className={`${styles.summaryBox} ${styles.summaryBoxMealAllowance}`}>
                  <div className={styles.summaryBoxLabel}>Phụ cấp</div>
                  <div className={styles.summaryBoxValue}>
                    {selectedEmployeeSummary.mealAllowance}
                  </div>
                </div>
                <div className={`${styles.summaryBox} ${styles.summaryBoxActualTotal}`}>
                  <div className={styles.summaryBoxLabel}>Tổng thực tế</div>
                  <div className={styles.summaryBoxValue}>
                    {selectedEmployeeSummary.actualTotal}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

